import { camelCase } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import classnames from '../../../../core/utils/classnames.tsx';

export const formatSkillKey = (skillKey: string, t: (key: string, options?: any) => string) => {
    const translationKey = camelCase(skillKey || '').replace('Skill', '');
    return t(`skills.${translationKey}.name`, { defaultValue: skillKey });
};

export const formatToolProviderKey = (key: string, t: (key: string, options?: any) => string) => {
    const translationKey = camelCase(key || '').replace('ToolProvider', '');
    return t(`toolProviders.${translationKey}.name`, { defaultValue: key });
};

const AgentCardContent = ({
    name,
    description,
    skillKeys,
    erpToolProviderKey,
    hasMasterData,
    hasOrderHistory,
    titleAddon,
    children,
    className,
    ...props
}: React.ComponentPropsWithRef<'div'> & {
    name: string;
    description?: string;
    skillKeys: string[];
    erpToolProviderKey?: string;
    hasMasterData?: boolean;
    hasOrderHistory?: boolean;
    titleAddon?: React.ReactNode;
}) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    return (
        <div className={classnames('flex flex-col gap-4', className)} {...props}>
            <div className="flex flex-col gap-1 flex-1">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="text-content-strong font-semibold">{name}</h3>
                    {titleAddon}
                </div>
                {description && <p className="text-sm text-content-medium">{description}</p>}
            </div>

            <div className="flex flex-col gap-1.5">
                {skillKeys.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {skillKeys.map((skillKey) => (
                            <span
                                key={skillKey}
                                className="px-2 py-0.5 text-sm rounded-full bg-surface-brand-medium-default text-content-strong"
                            >
                                {formatSkillKey(skillKey, t)}
                            </span>
                        ))}
                    </div>
                )}

                <div className="flex flex-wrap gap-1.5">
                    {erpToolProviderKey && (
                        <span className="px-2 py-0.5 text-sm rounded-full bg-surface-strong text-content-strong">
                            {formatToolProviderKey(erpToolProviderKey, t)}
                        </span>
                    )}

                    {hasMasterData && (
                        <span className="px-2 py-0.5 text-sm rounded-full bg-surface-strong text-content-strong">
                            {t('overview.masterDataConnected')}
                        </span>
                    )}

                    {hasOrderHistory && (
                        <span className="px-2 py-0.5 text-sm rounded-full bg-surface-strong text-content-strong">
                            {t('overview.orderHistoryEnabled')}
                        </span>
                    )}
                </div>
            </div>

            {children}
        </div>
    );
};

export default AgentCardContent;
